import type { VoidFunctionComponent } from 'react';

import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';

import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotFixBox } from './FossabotFixBox';
import { useFakeFossabotFlow } from './useFakeFossabotFlow';

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
export const FossabotRemediationPanel: VoidFunctionComponent = () => {
  const { data, isLoading, isError, error } = useGitHubAppInstallation('fossabot');
  const { mutate: startInstall } = useStartGitHubAppInstall();

  const isConnected = Boolean(data?.installed);

  // ⚠️ DEMO ONLY: fake happy-path driver so Create does something. Swap for the
  // real status query + create-PR mutation later. See useFakeFossabotFlow.
  const fakeFlow = useFakeFossabotFlow();

  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          <FossabotConnectionBanner
            isConnected={isConnected}
            isLoading={isLoading}
            isError={isError}
            errorMessage={getErrorMessage(error, 'Could not check fossabot connection status.')}
            onConnect={() => startInstall('fossabot')}
          />
          {/*
            The fossabot fix box only shows once connected. When not connected the
            panel shows the create-ticket upgrade options instead (built separately).
            Props come from the DEMO fake flow for now; the real PR status query and
            create-PR mutation are added in a later ticket.
          */}
          {isConnected && (
            <FossabotFixBox
              status={fakeFlow.status}
              vcsUrl={fakeFlow.vcsUrl}
              fossabotUrl={fakeFlow.fossabotUrl}
              onCreatePR={fakeFlow.onCreatePR}
            />
          )}
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
